import TronWeb from 'tronweb';

const tw = new TronWeb({ fullHost: 'https://api.trongrid.io' });
const TRONGRID = 'https://api.trongrid.io';
const SUN = 1000000;

function fetchWithTimeout(url, ms = 10000) {
  const ctrl = new AbortController();
  const tid = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { headers: { Accept: 'application/json' }, signal: ctrl.signal })
    .finally(() => clearTimeout(tid));
}

function hexToBase58(hex) {
  if (!hex) return '';
  try { return tw.address.fromHex(hex); } catch { return hex; }
}

async function fetchTrxTxsForAddress(address, limit = 200) {
  try {
    const res = await fetchWithTimeout(
      `${TRONGRID}/v1/accounts/${address}/transactions?limit=${limit}&only_confirmed=true`
    );
    const data = await res.json();
    return (data.data || [])
      .filter(tx => tx.raw_data?.contract?.[0]?.type === 'TransferContract')
      .map(tx => {
        const v = tx.raw_data.contract[0].parameter.value;
        return {
          hash: tx.txID,
          from: hexToBase58(v.owner_address),
          to: hexToBase58(v.to_address),
          amount: (v.amount || 0) / SUN,
          timestamp: tx.block_timestamp,
        };
      });
  } catch (e) {
    console.warn(`[graph] TX fetch failed for ${address}:`, e.message);
    return [];
  }
}

async function getTopSenders(address, topK) {
  const txs = await fetchTrxTxsForAddress(address);
  const map = new Map();
  for (const tx of txs) {
    if (tx.to !== address) continue;
    const e = map.get(tx.from) || { total: 0, txs: [] };
    e.total += tx.amount;
    e.txs.push(tx);
    map.set(tx.from, e);
  }
  return [...map.entries()]
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, topK)
    .map(([addr, info]) => ({ address: addr, totalAmount: info.total, txs: info.txs.slice(0, 5) }));
}

async function getTopRecipients(address, topK) {
  const txs = await fetchTrxTxsForAddress(address);
  const map = new Map();
  for (const tx of txs) {
    if (tx.from !== address) continue;
    const e = map.get(tx.to) || { total: 0, txs: [] };
    e.total += tx.amount;
    e.txs.push(tx);
    map.set(tx.to, e);
  }
  return [...map.entries()]
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, topK)
    .map(([addr, info]) => ({ address: addr, totalAmount: info.total, txs: info.txs.slice(0, 5) }));
}

async function fetchTrxBalance(address) {
  try {
    const res = await fetchWithTimeout(`${TRONGRID}/v1/accounts/${address}`, 6000);
    const data = await res.json();
    return ((data.data || [])[0]?.balance || 0) / SUN;
  } catch { return 0; }
}

/**
 * Build a TRX transaction graph centered on startAddress.
 *   n  = levels backward (who funded this address)
 *   m  = levels forward  (where this address sent funds)
 *   maxPerLevel  = max unique counterparties per node per direction
 *   maxTotalNodes = hard cap on total nodes (prevents explosion)
 * onProgress(event) is called after each BFS level and after balance fetch.
 */
export async function buildTxGraph(
  startAddress,
  { n = 2, m = 2, maxPerLevel = 8, maxTotalNodes = 120 } = {},
  onProgress
) {
  const nodeMap = new Map();  // address → node object
  const edgeList = [];
  const edgeIds = new Set();
  const visited = new Set();

  function addNode(address, level, direction) {
    if (!nodeMap.has(address)) {
      nodeMap.set(address, {
        id: address,
        label: address.slice(0, 6) + '…' + address.slice(-4),
        level,
        direction,   // 'origin' | 'before' | 'after'
        totalIn: 0,
        totalOut: 0,
        txCount: 0,
        balance: 0,
      });
    }
  }

  function addEdges(from, to, txs) {
    for (const tx of txs) {
      if (!edgeIds.has(tx.hash)) {
        edgeIds.add(tx.hash);
        edgeList.push({
          id: tx.hash,
          source: from,
          target: to,
          amount: tx.amount,
          timestamp: tx.timestamp,
          hash: tx.hash,
        });
      }
    }
    const totalAmt = txs.reduce((s, t) => s + t.amount, 0);
    const fn = nodeMap.get(from); if (fn) { fn.totalOut += totalAmt; fn.txCount += txs.length; }
    const tn = nodeMap.get(to);   if (tn) { tn.totalIn  += totalAmt; tn.txCount += txs.length; }
  }

  // Seed
  addNode(startAddress, 0, 'origin');
  visited.add(startAddress);

  // ── Backward BFS: who sent funds TO these addresses ──
  let backQueue = [startAddress];
  for (let lvl = 1; lvl <= n; lvl++) {
    if (!backQueue.length || nodeMap.size >= maxTotalNodes) break;
    const cap = Math.max(1, maxTotalNodes - nodeMap.size);
    const slice = backQueue.slice(0, cap);
    const results = await Promise.allSettled(slice.map(addr => getTopSenders(addr, maxPerLevel)));
    const nextQueue = [];

    for (let i = 0; i < slice.length; i++) {
      if (nodeMap.size >= maxTotalNodes) break;
      if (results[i].status !== 'fulfilled') continue;
      for (const sender of results[i].value) {
        if (nodeMap.size >= maxTotalNodes) break;
        addNode(sender.address, -lvl, 'before');
        addEdges(sender.address, slice[i], sender.txs);
        if (!visited.has(sender.address)) {
          visited.add(sender.address);
          nextQueue.push(sender.address);
        }
      }
    }
    onProgress?.({ phase: 'before', level: lvl, of: n, nodes: nodeMap.size });
    backQueue = nextQueue;
  }

  // ── Forward BFS: where funds went FROM these addresses ──
  let fwdQueue = [startAddress];
  for (let lvl = 1; lvl <= m; lvl++) {
    if (!fwdQueue.length || nodeMap.size >= maxTotalNodes) break;
    const cap = Math.max(1, maxTotalNodes - nodeMap.size);
    const slice = fwdQueue.slice(0, cap);
    const results = await Promise.allSettled(slice.map(addr => getTopRecipients(addr, maxPerLevel)));
    const nextQueue = [];

    for (let i = 0; i < slice.length; i++) {
      if (nodeMap.size >= maxTotalNodes) break;
      if (results[i].status !== 'fulfilled') continue;
      for (const recip of results[i].value) {
        if (nodeMap.size >= maxTotalNodes) break;
        addNode(recip.address, lvl, 'after');
        addEdges(slice[i], recip.address, recip.txs);
        if (!visited.has(recip.address)) {
          visited.add(recip.address);
          nextQueue.push(recip.address);
        }
      }
    }
    onProgress?.({ phase: 'after', level: lvl, of: m, nodes: nodeMap.size });
    fwdQueue = nextQueue;
  }

  // ── Fetch balances in parallel (best-effort) ──
  onProgress?.({ phase: 'balances', nodes: nodeMap.size });
  const addresses = [...nodeMap.keys()];
  const bals = await Promise.allSettled(addresses.map(fetchTrxBalance));
  addresses.forEach((addr, i) => {
    if (bals[i].status === 'fulfilled') nodeMap.get(addr).balance = bals[i].value;
  });

  onProgress?.({ phase: 'done', nodes: nodeMap.size, edges: edgeList.length });

  return { nodes: [...nodeMap.values()], edges: edgeList };
}
